import {
  type Alerte,
  cleLivreSchema,
  type DeclarationTexte,
  type DoublePlan,
  type Gabarit,
  type ModeJournal,
  type MotInterdit,
  type OrigineJournal,
  type Provenance,
  type Trouvaille,
  type TypeDouble,
} from "./contract";
import { compter } from "./format";

/**
 * Les textes et les calculs purs du module Histoires (#226) : le plan d'un livre (numero de
 * chaque double `histoire`, ses pages), les titres, les libelles de provenance et du journal,
 * les dates en clair, la cle proposee depuis un titre, les morceaux du calque des mots
 * interdits. Aucune horloge implicite : l'instant courant est passe en argument.
 *
 * Compter les mots, trouver les mots interdits et calculer les alertes ne sont pas ici : ce
 * sont les regles partagees avec le serveur (`histoires-texte.ts` du contrat).
 */

// ---------------------------------------------------------------------------------------
// Le plan d'un livre.

/** Ce qu'il faut d'une double pour la placer dans le livre. */
export type DoubleDuPlan = {
  readonly id: number;
  readonly rang: number;
  readonly type: TypeDouble;
  readonly pages: number;
};

/** Les pages d'une double : une page (`debut === fin`) ou deux. */
export type Pages = { readonly debut: number; readonly fin: number };

export type PlanDuLivre = {
  /** Le numero de chaque double `histoire`, par identifiant : son rang parmi elles, depuis 1. */
  readonly numeros: ReadonlyMap<number, number>;
  /** Les pages de chaque double, par identifiant ; `null` pour la couverture, qui n'en a pas. */
  readonly pages: ReadonlyMap<number, Pages | null>;
  /** Le nombre de doubles `histoire`. */
  readonly histoires: number;
  /** Le nombre de pages du livre, couverture exclue. */
  readonly totalPages: number;
};

/**
 * Le plan d'un livre, dans l'ordre des rangs : « 3. Elle est fatiguée » est la troisieme double
 * `histoire` (le titre est range sans numero), et la page 1 suit la couverture.
 */
export function planDuLivre(doubles: readonly DoubleDuPlan[]): PlanDuLivre {
  const ordre = [...doubles].sort((a, b) => a.rang - b.rang);
  const numeros = new Map<number, number>();
  const pages = new Map<number, Pages | null>();
  let page = 1;
  for (const double of ordre) {
    if (double.type === "histoire") numeros.set(double.id, numeros.size + 1);
    if (double.pages <= 0) {
      pages.set(double.id, null);
      continue;
    }
    pages.set(double.id, { debut: page, fin: page + double.pages - 1 });
    page += double.pages;
  }
  return { numeros, pages, histoires: numeros.size, totalPages: page - 1 };
}

/** « p. 1 », « p. 2 et 3 » : les pages d'une puce ou de la zone grise. */
export function pagesCourtes(pages: Pages): string {
  return pages.debut === pages.fin ? `p. ${pages.debut}` : `p. ${pages.debut} et ${pages.fin}`;
}

/** « page 1 », « pages 8 et 9 » : les pages sous le titre d'une double. */
export function pagesLongues(pages: Pages): string {
  return pages.debut === pages.fin ? `page ${pages.debut}` : `pages ${pages.debut} et ${pages.fin}`;
}

const LIBELLE_TYPE: Readonly<Record<TypeDouble, string>> = {
  couverture: "Couverture",
  titre: "Titre",
  liminaire: "Liminaire",
  histoire: "Double",
  note: "Page pour les parents",
  blanche: "Page blanche",
};

/** Le nom d'une double hors du recit, quand elle n'a pas de titre a elle. */
export function libelleType(type: TypeDouble): string {
  return LIBELLE_TYPE[type];
}

/**
 * Le titre d'une double : « 3. Elle est fatiguée » pour une double `histoire` (le numero vient
 * du plan), « Double 3 » sans titre, et le titre ou le nom du type pour les autres. Sans plan
 * (le livre pas encore lu), le titre seul.
 */
export function titreDouble(
  double: { readonly type: TypeDouble; readonly titre: string | null },
  numero: number | undefined,
): string {
  const titre = double.titre?.trim() ?? "";
  if (double.type !== "histoire") return titre === "" ? libelleType(double.type) : titre;
  if (numero === undefined) return titre === "" ? "Double" : titre;
  return titre === "" ? `Double ${numero}` : `${numero}. ${titre}`;
}

/**
 * Ce qu'on dit du plan d'un livre neuf avant de le creer : les doubles du recit groupees, les
 * autres par leur nom, avec leurs pages. Calcule depuis le plan du contrat, pour que la phrase
 * reste vraie le jour ou il change.
 */
export function planEnClair(plan: readonly DoublePlan[]): string {
  const doubles = plan.map((double, rang) => ({ ...double, id: rang, rang }));
  const lu = planDuLivre(doubles);
  const morceaux: string[] = [];
  let groupe: Pages[] = [];
  const fermerLeGroupe = (): void => {
    const premiere = groupe[0];
    const derniere = groupe[groupe.length - 1];
    if (premiere !== undefined && derniere !== undefined) {
      morceaux.push(
        `${compter(groupe.length, "double", "doubles")} (p. ${premiere.debut} à ${derniere.fin})`,
      );
    }
    groupe = [];
  };
  for (const double of doubles) {
    const pages = lu.pages.get(double.id) ?? null;
    if (double.type === "histoire" && pages !== null) {
      groupe.push(pages);
      continue;
    }
    fermerLeGroupe();
    const nom = libelleType(double.type).toLocaleLowerCase("fr");
    morceaux.push(pages === null ? nom : `${nom} (${pagesCourtes(pages)})`);
  }
  fermerLeGroupe();
  return `${morceaux.join(", ")}, soit ${lu.totalPages} pages`;
}

// ---------------------------------------------------------------------------------------
// La provenance du texte et la declaration IA.

export const LIBELLE_PROVENANCE: Readonly<Record<Provenance, string>> = {
  ecrit: "écrit",
  assiste: "assisté",
  genere: "généré",
};

/** La legende du chemin de fer : ce que dit chaque badge. */
export const SENS_PROVENANCE: Readonly<Record<Provenance | "vide", string>> = {
  ecrit: "par toi",
  assiste: "ton texte, réécrit par Claude",
  genere: "écrit par Claude, à déclarer à KDP",
  vide: "pas encore de texte",
};

/** La phrase de declaration d'une double, sous son texte (plan, decision 14). */
export function declarationDeLaDouble(provenance: Provenance | null): string {
  switch (provenance) {
    case "ecrit":
      return "Écrit : ton texte. Rien à déclarer à KDP.";
    case "assiste":
      return "Assisté : ton texte, réécrit par Claude. Rien à déclarer à KDP.";
    case "genere":
      return "Généré : écrit par Claude, à déclarer à KDP.";
    default:
      return "Pas encore de texte : ce que tu écriras sera « écrit ».";
  }
}

/** La puce du livre (spec, section 2) : ce que le texte du livre declare a KDP. */
export function declarationDuLivre(declaration: DeclarationTexte): string {
  switch (declaration) {
    case "genere":
      return "Texte : généré, à déclarer à KDP";
    case "assiste":
      return "Texte : assisté, rien à déclarer à KDP";
    default:
      return "Texte : rien à déclarer à KDP";
  }
}

// ---------------------------------------------------------------------------------------
// Le journal du texte.

export const LIBELLE_ORIGINE: Readonly<Record<OrigineJournal, string>> = {
  nicolas: "toi",
  claude: "Claude",
};

export const LIBELLE_MODE: Readonly<Record<ModeJournal, string>> = {
  saisie: "saisie",
  import: "manuscrit collé",
  propose: "proposition",
  rewrite: "réécriture",
  restauration: "restauration",
  effacement: "repartir de zéro",
};

// Les dates en clair sont communes aux modules depuis les livres-jeux (#276) : `dates-en-clair.ts`.
export { heure, ilYA, modifieEnClair, quandEnClair, saisieDe } from "./dates-en-clair";

// ---------------------------------------------------------------------------------------
// La cle d'un livre neuf.

/**
 * La cle proposee depuis le titre : minuscules sans accents, chiffres, et un tiret a la place
 * de tout le reste. Elle se modifie ensuite, et `cleLivreSchema` a le dernier mot.
 */
export function cleDepuisTitre(titre: string): string {
  const cle = titre
    .toLocaleLowerCase("fr")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replaceAll("œ", "oe")
    .replaceAll("æ", "ae")
    .replaceAll("ß", "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cle.slice(0, 120).replace(/-+$/, "");
}

/**
 * Pourquoi une cle ne passe pas, ou `null` quand `cleLivreSchema` l'accepte. Le schema du
 * contrat decide ; seul le message est d'ici.
 */
export function refusDeCle(cle: string): string | null {
  if (cle === "") return "La clé est vide : elle se remplit depuis le titre.";
  const lu = cleLivreSchema.safeParse(cle);
  if (lu.success) return null;
  if (lu.error.issues.some((probleme) => probleme.code === "custom")) {
    return "Ce nom est réservé par Windows : le dossier ne pourrait pas se créer.";
  }
  return "Minuscules, chiffres et tirets seulement, une lettre ou un chiffre en premier, 120 caractères au plus.";
}

// ---------------------------------------------------------------------------------------
// Les mots interdits.

/** Un morceau du calque : du texte tel quel, ou un mot interdit a surligner. */
export type Morceau = { readonly texte: string; readonly interdit: boolean };

/**
 * Le texte coupe pour le calque, aux positions que rend `motsInterditsDans` : deux trouvailles
 * qui se chevauchent (« elle dort » et « dort ») ne font qu'un surlignage. Les morceaux mis bout
 * a bout redonnent le texte exact, caractere pour caractere : le calque ne decale rien.
 */
export function morceauxDuCalque(texte: string, trouvailles: readonly Trouvaille[]): Morceau[] {
  const plages: { debut: number; fin: number }[] = [];
  for (const trouvaille of [...trouvailles].sort((a, b) => a.debut - b.debut)) {
    const derniere = plages[plages.length - 1];
    if (derniere !== undefined && trouvaille.debut <= derniere.fin) {
      derniere.fin = Math.max(derniere.fin, trouvaille.fin);
    } else {
      plages.push({ debut: trouvaille.debut, fin: trouvaille.fin });
    }
  }
  const morceaux: Morceau[] = [];
  let curseur = 0;
  for (const plage of plages) {
    if (plage.debut > curseur) {
      morceaux.push({ texte: texte.slice(curseur, plage.debut), interdit: false });
    }
    morceaux.push({ texte: texte.slice(plage.debut, plage.fin), interdit: true });
    curseur = plage.fin;
  }
  if (curseur < texte.length) morceaux.push({ texte: texte.slice(curseur), interdit: false });
  return morceaux;
}

/** La raison d'un mot interdit trouve, telle que le brief la donne. */
export function raisonDe(mot: string, interdits: readonly MotInterdit[]): string | null {
  const trouve = interdits.find((interdit) => interdit.mot.trim() === mot);
  const raison = trouve?.raison?.trim() ?? "";
  return raison === "" ? null : raison;
}

/** Les mots interdits du brief groupes par raison, dans l'ordre de leur premiere apparition. */
export function groupesParRaison(
  interdits: readonly MotInterdit[],
): { readonly raison: string | null; readonly mots: readonly string[] }[] {
  const groupes: { raison: string | null; mots: string[] }[] = [];
  for (const interdit of interdits) {
    const raison = interdit.raison?.trim() || null;
    const groupe = groupes.find((candidat) => candidat.raison === raison);
    if (groupe === undefined) groupes.push({ raison, mots: [interdit.mot] });
    else groupe.mots.push(interdit.mot);
  }
  return groupes;
}

/** Une alerte sur une carte du chemin de fer : « mot interdit : « endormie » », « 112 mots, au plus 100 ». */
export function alerteEnClair(alerte: Alerte): string {
  if (alerte.type === "mot_interdit") {
    const mots = alerte.mots.map((mot) => `« ${mot} »`).join(", ");
    return `${alerte.mots.length > 1 ? "mots interdits" : "mot interdit"} : ${mots}`;
  }
  return alerte.mots > alerte.max
    ? `${compter(alerte.mots, "mot", "mots")}, au plus ${alerte.max}`
    : `${compter(alerte.mots, "mot", "mots")}, au moins ${alerte.min}`;
}

/** Le texte hors de la fourchette, sous le compteur de la double. */
export function horsFourchetteEnClair(
  alerte: Extract<Alerte, { type: "hors_fourchette" }>,
): string {
  if (alerte.mots > alerte.max) {
    return `${compter(alerte.mots - alerte.max, "mot", "mots")} de trop : le brief dit au plus ${alerte.max} par double, jamais au-delà`;
  }
  return `${compter(alerte.min - alerte.mots, "mot", "mots")} de moins que le minimum : le brief dit au moins ${alerte.min} par double`;
}

// ---------------------------------------------------------------------------------------
// Coller un manuscrit.

/** Des numeros en plages : `[7, 8, 9, 12]` donne « 7 à 9, 12 », `[5, 6]` donne « 5 et 6 ». */
export function plages(numeros: readonly number[]): string {
  const tries = [...new Set(numeros)].sort((a, b) => a - b);
  const morceaux: string[] = [];
  let debut: number | undefined;
  let precedent: number | undefined;
  const fermer = (): void => {
    if (debut === undefined || precedent === undefined) return;
    if (debut === precedent) morceaux.push(String(debut));
    else if (precedent === debut + 1) morceaux.push(`${debut} et ${precedent}`);
    else morceaux.push(`${debut} à ${precedent}`);
  };
  for (const numero of tries) {
    if (precedent !== undefined && numero === precedent + 1) {
      precedent = numero;
      continue;
    }
    fermer();
    debut = numero;
    precedent = numero;
  }
  fermer();
  return morceaux.join(", ");
}

// ---------------------------------------------------------------------------------------
// Les gabarits.

export const GABARITS: readonly Gabarit[] = [
  "bandeau_bas",
  "bandeau_haut",
  "texte_gauche",
  "texte_droite",
  "image_gauche",
  "image_droite",
  "texte_seul",
];

export const LIBELLE_GABARIT: Readonly<Record<Gabarit, string>> = {
  bandeau_bas: "Bandeau bas",
  bandeau_haut: "Bandeau haut",
  texte_gauche: "Texte à gauche",
  texte_droite: "Texte à droite",
  image_gauche: "Image à gauche",
  image_droite: "Image à droite",
  texte_seul: "Texte seul",
};

/** Les fins de ligne en `\n` seulement : commun a la console depuis #276 (`sauvegarde-texte.ts`). */
export { normaliser } from "./sauvegarde-texte";
